import { createServerFn } from "@tanstack/react-start";
import fallback from "../../content/legal-fallback.json";

export type LegalLang = "en" | "es" | "no";

export interface LegalSection {
  heading: string;
  body: string[];
}
export interface LegalPage {
  title: string;
  updated: string;
  sections: LegalSection[];
}
export interface LegalOperator {
  name: string;
  address: string;
  businessId: string;
  contactEmail: string;
}
export interface LegalPayload {
  website: string;
  language: LegalLang;
  supportedLanguages: LegalLang[];
  operator: LegalOperator;
  options: Record<string, boolean>;
  pages: {
    cookies: LegalPage;
    privacy: LegalPage;
    terms: LegalPage;
  };
}

const TTL_MS = 600_000; // 10 min
const cache = new Map<LegalLang, { at: number; data: LegalPayload }>();

async function fetchFromBlackbase(lang: LegalLang): Promise<LegalPayload> {
  const res = await fetch(
    `https://api.blackbase.io/functions/v1/legal-pages?lang=${lang}`,
    {
      headers: {
        Origin: "https://bizaclia.com",
        "Accept-Language": lang,
      },
    },
  );
  if (!res.ok) throw new Error(`legal-pages HTTP ${res.status}`);
  return (await res.json()) as LegalPayload;
}

export const getLegalPages = createServerFn({ method: "GET" })
  .inputValidator((input: { lang?: string } | undefined) => {
    const raw = (input?.lang ?? "es").toLowerCase();
    const lang: LegalLang =
      raw === "en" || raw === "es" || raw === "no" ? raw : "es";
    return { lang };
  })
  .handler(async ({ data }): Promise<LegalPayload> => {
    const { lang } = data;
    const now = Date.now();
    const hit = cache.get(lang);
    if (hit && now - hit.at < TTL_MS) return hit.data;

    try {
      const fresh = await fetchFromBlackbase(lang);
      cache.set(lang, { at: now, data: fresh });
      return fresh;
    } catch (err) {
      console.error("[legal] fetch failed, using fallback", err);
      if (hit) return hit.data;
      return fallback as unknown as LegalPayload;
    }
  });
